import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

const DragContext = createContext(null);
export const useDrag = () => useContext(DragContext);

export function DragProvider({ children }) {
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const zonesRef = useRef(new Map());
  const moveRef = useRef(null);
  const upRef = useRef(null);

  const registerZone = useCallback((id, data) => {
    zonesRef.current.set(id, data);
    return () => zonesRef.current.delete(id);
  }, []);

  const startDrag = useCallback((payload, ghost, e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault?.();
    e.stopPropagation?.();
    const d = { payload, ghost, x: e.clientX, y: e.clientY };
    dragRef.current = d;
    setDrag(d);

    const onMove = (ev) => {
      if (!dragRef.current) return;
      if (ev.cancelable) ev.preventDefault();
      dragRef.current = { ...dragRef.current, x: ev.clientX, y: ev.clientY };
      setDrag(dragRef.current);
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", moveRef.current);
      window.removeEventListener("pointerup", upRef.current);
      window.removeEventListener("pointercancel", upRef.current);
      const d2 = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d2) return;
      const px = ev.clientX, py = ev.clientY;
      let best = null, bestArea = Infinity;
      zonesRef.current.forEach((zone) => {
        const el = zone.el?.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) {
          const area = r.width * r.height;
          if (area < bestArea) { bestArea = area; best = { zone, r }; }
        }
      });
      if (best) best.zone.onDrop(d2.payload, { clientX: px, clientY: py, rect: best.r });
    };

    moveRef.current = onMove;
    upRef.current = onUp;
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, []);

  return (
    <DragContext.Provider value={{ startDrag, registerZone, dragging: !!drag }}>
      {children}
      {drag &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: drag.x,
              top: drag.y,
              transform: "translate(-50%, -50%) scale(1.05)",
              pointerEvents: "none",
              zIndex: 9999,
              opacity: 0.92,
            }}
          >
            {drag.ghost}
          </div>,
          document.body
        )}
    </DragContext.Provider>
  );
}

export function useDropZone(onDrop) {
  const { registerZone } = useDrag();
  const ref = useRef(null);
  const idRef = useRef("zone-" + Math.random().toString(36).slice(2));
  const cbRef = useRef(onDrop);
  cbRef.current = onDrop;
  const attach = useCallback((node) => {
    ref.current = node;
  }, []);
  useEffect(() => {
    return registerZone(idRef.current, { el: ref, onDrop: (p, i) => cbRef.current(p, i) });
  }, [registerZone]);
  return attach;
}
